import { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import Link from 'next/link';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../../../lib/firebase';
import {
    BadgeCheck,
    MessageCircle,
    PenSquare,
    ShieldCheck,
    Star,
    Users,
    Video
} from 'lucide-react';

export default function Testimonials() {
    const { t } = useTranslation();

    const TRUST_HIGHLIGHTS = [
        {
            icon: BadgeCheck,
            title: t("home.testimonials.verifiedTitle", "Every Maven Is Verified"),
            description: t("home.testimonials.verifiedDesc", "We manually review applications and run Stripe identity checks before anyone takes a call."),
            bullets: [
                t("home.testimonials.verifiedBullet1", "Human review of credentials and experience"),
                t("home.testimonials.verifiedBullet2", "Stripe Connect identity + payout verification"),
                t("home.testimonials.verifiedBullet3", "Ongoing quality audits on active mavens")
            ]
        },
        {
            icon: ShieldCheck,
            title: t("home.testimonials.paymentsTitle", "Payments Never Go Missing"),
            description: t("home.testimonials.paymentsDesc", "Calls are tracked server-side to the second, so mavens are paid and seekers only pay for real talk time."),
            bullets: [
                t("home.testimonials.paymentsBullet1", "Server timestamping across web and mobile"),
                t("home.testimonials.paymentsBullet2", "Automatic billing fail-safes for drops or crashes"),
                t("home.testimonials.paymentsBullet3", "Billing starts immediately when the Maven picks up")
            ]
        },
        {
            icon: Star,
            title: t("home.testimonials.reviewsTitle", "Two-Sided Reviews After Every Call"),
            description: t("home.testimonials.reviewsDesc", "Seekers and mavens both leave ratings that stay attached to their profile history."),
            bullets: [
                t("home.testimonials.reviewsBullet1", "Review prompts at the end of every session"),
                t("home.testimonials.reviewsBullet2", "Contextual feedback visible before the next call"),
                t("home.testimonials.reviewsBullet3", "Flagged calls automatically routed to our team")
            ]
        },
        {
            icon: Users,
            title: t("home.testimonials.supportTitle", "Real Humans Ready To Help"),
            description: t("home.testimonials.supportDesc", "Our success team monitors every cohort while we scale, so questions get answered fast."),
            bullets: [
                t("home.testimonials.supportBullet1", "Dedicated onboarding for early mavens"),
                t("home.testimonials.supportBullet2", "Response within 24 hours, 7 days a week"),
                t("home.testimonials.supportBullet3", "Escalation path for billing or quality disputes")
            ]
        }
    ];

    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [testimonialsData, setTestimonialsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFallbackMode, setIsFallbackMode] = useState(false);

    // Fetch approved testimonials from Firestore
    useEffect(() => {
        const fetchTestimonials = async () => {
            try {
                const q = query(
                    collection(db, 'testimonials'),
                    where('status', '==', 'approved'),
                    where('allowPublicDisplay', '==', true),
                    orderBy('approvedAt', 'desc'),
                    limit(20)
                );

                const snapshot = await getDocs(q);
                const approved = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                // If we have real testimonials, use them; otherwise fall back to examples
                if (approved.length > 0) {
                    setTestimonialsData(approved);
                    setIsFallbackMode(false);
                } else {
                    setTestimonialsData([]);
                    setIsFallbackMode(true);
                }
                setLoading(false);
            } catch (error) {
                console.error('Error fetching testimonials:', error);
                setTestimonialsData([]);
                setIsFallbackMode(true);
                setLoading(false);
            }
        };

        fetchTestimonials();
    }, []);

    // Auto-rotate testimonials every 4 seconds (faster with more testimonials)
    useEffect(() => {
        if (isFallbackMode || testimonialsData.length <= 1) return;
        const interval = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonialsData.length);
        }, 4000);
        return () => clearInterval(interval);
    }, [isFallbackMode, testimonialsData.length]);

    const hasTestimonials = !isFallbackMode && testimonialsData.length > 0;

    return (
        <>
            <style>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-20px); }
                }
                @keyframes fadeSlide {
                    0% { opacity: 0; transform: translateX(30px); }
                    100% { opacity: 1; transform: translateX(0); }
                }
                .testimonial-card {
                    animation: fadeSlide 0.6s ease-out;
                }
                .floating-element {
                    animation: float 6s ease-in-out infinite;
                }
            `}</style>
            
            <main className="relative py-12 px-6 bg-gradient-to-br from-[#f8f5f0] via-[#f1ebe3] to-[#efe6d8] overflow-hidden" id="testimonials">
                {/* Background decorations */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-10 right-10 w-32 h-32 bg-gradient-to-br from-emerald-200 to-amber-200 rounded-full opacity-25 floating-element"></div>
                    <div className="absolute bottom-20 left-10 w-24 h-24 bg-gradient-to-br from-amber-200 to-rose-200 rounded-full opacity-25 floating-element" style={{animationDelay: '2s'}}></div>
                </div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="text-center mb-16 max-w-3xl mx-auto">
                        <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-emerald-100 to-amber-100 text-emerald-900 text-sm font-medium mb-4">
                            {isFallbackMode ? (
                                <>
                                    <ShieldCheck className="w-4 h-4 mr-2" />
                                    {t("home.testimonials.eyebrowTrust", "Built for trust")}
                                </>
                            ) : (
                                <>
                                    <MessageCircle className="w-4 h-4 mr-2" />
                                    {t("home.testimonials.eyebrowFeedback", "Early member feedback")}
                                </>
                            )}
                        </div>
                        <h2 className="text-4xl md:text-6xl font-black leading-tight text-gray-900 mb-4 tracking-tight">
                            {isFallbackMode ? (
                                <>
                                    {t("home.testimonials.headingWhyMembers", "Why Members")}
                                    <span className="relative inline-block align-middle mx-4">
                                        <span
                                            className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-teal-600 to-amber-500 rounded-2xl"
                                            style={{
                                                transform: "rotate(-2deg)",
                                                zIndex: 1,
                                                left: "-12px",
                                                right: "-12px",
                                                top: "-8px",
                                                bottom: "-8px",
                                            }}
                                        ></span>
                                        <span className="relative text-white px-6 py-2 font-black z-10">
                                            {t("home.testimonials.headingTrust", "TRUST")}
                                        </span>
                                    </span>
                                    Camyvera
                                </>
                            ) : (
                                <>
                                    {t("home.testimonials.headingHowIt", "How It")}
                                    <span className="relative inline-block align-middle mx-4">
                                        <span
                                            className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-teal-600 to-amber-500 rounded-2xl"
                                            style={{
                                                transform: "rotate(-2deg)",
                                                zIndex: 1,
                                                left: "-12px",
                                                right: "-12px",
                                                top: "-8px",
                                                bottom: "-8px",
                                            }}
                                        ></span>
                                        <span className="relative text-white px-6 py-2 font-black z-10">
                                            {t("home.testimonials.headingWorks", "WORKS")}
                                        </span>
                                    </span>
                                </>
                            )}
                        </h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            {isFallbackMode
                                ? t("home.testimonials.subtitleFallback", "We're onboarding more seekers and mavens right now—here are the guardrails we put in place before the first story goes live.")
                                : t("home.testimonials.subtitleReal", "Real examples from beta users testing the platform")}
                        </p>
                        {hasTestimonials && (
                            <div className="mt-4 text-sm text-gray-500">
                                {t("home.testimonials.showingCount", "Showing {{shown}} of {{total}} examples", { shown: Math.min(activeTestimonial + 3, testimonialsData.length), total: testimonialsData.length })}
                            </div>
                        )}
                    </div>

                    {hasTestimonials ? (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                                {testimonialsData.slice(activeTestimonial, activeTestimonial + 3)
                                    .concat(activeTestimonial + 3 > testimonialsData.length 
                                        ? testimonialsData.slice(0, (activeTestimonial + 3) % testimonialsData.length)
                                        : [])
                                    .slice(0, 3)
                                    .map((testimonial, displayIndex) => {
                                        const actualIndex = (activeTestimonial + displayIndex) % testimonialsData.length;
                                        const isActive = displayIndex === 1;
                                        return (
                                            <div
                                                key={actualIndex}
                                                className={`testimonial-card relative p-8 rounded-3xl backdrop-blur-lg border transition-all duration-700 cursor-pointer hover:scale-105 ${
                                                    isActive 
                                                        ? 'bg-gradient-to-br from-white/40 to-white/20 border-white/30 shadow-2xl scale-105'
                                                        : 'bg-white/20 border-white/20 shadow-lg hover:bg-white/30'
                                                }`}
                                                onClick={() => setActiveTestimonial(actualIndex)}
                                                style={{
                                                    backdropFilter: 'blur(20px)',
                                                    WebkitBackdropFilter: 'blur(20px)'
                                                }}
                                            >
                                                <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-emerald-500 to-amber-500 rounded-2xl flex items-center justify-center shadow-lg">
                                                    <span className="text-white text-2xl">"</span>
                                                </div>
                                                <div className="flex items-center gap-1 mb-4">
                                                    {[...Array(testimonial.rating)].map((_, i) => (
                                                        <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                                                    ))}
                                                </div>
                                                <p className="text-gray-800 text-lg font-medium mb-6 italic leading-relaxed">
                                                    "{testimonial.message}"
                                                </p>
                                                <div className="flex items-center gap-4">
                                                    {testimonial.photoUrl ? (
                                                        <img
                                                            src={testimonial.photoUrl}
                                                            alt={testimonial.displayName || testimonial.author}
                                                            className="w-12 h-12 rounded-2xl object-cover border-2 border-white shadow-lg"
                                                        />
                                                    ) : (
                                                        <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-amber-400 rounded-2xl flex items-center justify-center text-2xl">
                                                            {testimonial.avatar || (testimonial.displayName || testimonial.author).charAt(0)}
                                                        </div>
                                                    )}
                                                    <div>
                                                        <div className="font-bold text-gray-900">{testimonial.displayName || testimonial.author}</div>
                                                        <div className="text-gray-600 text-sm">{testimonial.title}</div>
                                                    </div>
                                                    <div className="ml-auto">
                                                        {testimonial.earnings && (
                                                            <div className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                                                                {testimonial.earnings}
                                                            </div>
                                                        )}
                                                        {testimonial.savings && (
                                                            <div className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                                                                {testimonial.savings}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                {testimonial.videoUrl && (
                                                    <div className="mt-4">
                                                        <a
                                                            href={testimonial.videoUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-emerald-700 hover:text-emerald-900 font-semibold text-sm flex items-center gap-2"
                                                        >
                                                            <Video className="w-4 h-4" />
                                                            {t("home.testimonials.watchVideo", "Watch Video →")}
                                                        </a>
                                                    </div>
                                                )}
                                                {isActive && (
                                                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-gradient-to-r from-emerald-500 to-amber-500 rounded-full"></div>
                                                )}
                                            </div>
                                        );
                                    })}
                            </div>

                            <div className="flex justify-center gap-3 mt-12">
                                {testimonialsData.map((testimonial, index) => (
                                    <button
                                        key={testimonial.id || index}
                                        onClick={() => setActiveTestimonial(index)}
                                        className={`w-3 h-3 rounded-full transition-all duration-300 ${
                                            index === activeTestimonial
                                                ? 'bg-gradient-to-r from-emerald-500 to-amber-500 scale-125'
                                                : 'bg-gray-300 hover:bg-gray-400'
                                        }`}
                                        aria-label={t("home.testimonials.viewTestimonialAria", "View testimonial from {{name}}, {{title}}", { name: testimonial.displayName || testimonial.author, title: testimonial.title })}
                                    />
                                ))}
                            </div>

                            <div className="mt-16 text-center">
                                <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-3xl p-10 max-w-3xl mx-auto">
                                    <h3 className="text-3xl font-bold text-white mb-4">
                                        {t("home.testimonials.shareTitle", "Share Your Experience")}
                                    </h3>
                                    <p className="text-xl text-emerald-100 mb-6">
                                        {t("home.testimonials.shareSubtitle", "Help others discover Camyvera by sharing your story")}
                                    </p>
                                    <a
                                        href="/submit-testimonial"
                                        className="inline-flex items-center gap-2 bg-white text-emerald-700 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-emerald-50 transition-all duration-300 hover:scale-105 shadow-xl"
                                    >
                                        <PenSquare className="w-5 h-5" />
                                        {t("home.testimonials.submitButton", "Submit Your Testimonial")}
                                    </a>
                                    <p className="text-sm text-emerald-200 mt-4">
                                        {t("home.testimonials.submitNote", "(Login required • Reviewed within 24-48 hours)")}
                                    </p>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 max-w-6xl mx-auto">
                                {TRUST_HIGHLIGHTS.map((item) => {
                                    const Icon = item.icon;
                                    return (
                                    <div
                                        key={item.title}
                                        className="relative bg-white/80 border border-white/60 rounded-3xl p-8 shadow-xl shadow-emerald-100/60 transition-transform duration-500 hover:-translate-y-2"
                                    >
                                        <div className="w-10 h-10 mb-4 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
                                            <Icon className="w-5 h-5" />
                                        </div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                                        <p className="text-sm text-gray-600 leading-relaxed mb-4">{item.description}</p>
                                        <ul className="space-y-2 text-sm text-gray-600">
                                            {item.bullets.map((bullet, idx) => (
                                                <li key={idx} className="flex items-start gap-2">
                                                    <span className="text-emerald-600 mt-0.5">•</span>
                                                    <span>{bullet}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <div className="absolute inset-0 -z-10 rounded-3xl bg-gradient-to-br from-emerald-500/5 via-amber-500/5 to-emerald-500/5 blur-xl"></div>
                                    </div>
                                    );
                                })}
                            </div>
                            <div className="mt-12 text-center">
                                <p className="text-base text-gray-600 mb-6">
                                    {t("home.testimonials.fallbackCtaLead", "Want to be one of the first stories? Join the community today and your review will surface as soon as it’s verified.")}
                                </p>
                                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                    <Link
                                        href="/expert/onboarding"
                                        className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-emerald-600 text-white font-semibold shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition"
                                    >
                                        {t("home.testimonials.joinAsSeeker", "Join as a Seeker")}
                                    </Link>
                                    <Link
                                        href="/expert/onboarding"
                                        className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-white border border-emerald-200 text-emerald-700 font-semibold shadow-lg shadow-emerald-100 hover:bg-emerald-50 transition"
                                    >
                                        {t("home.testimonials.applyAsMaven", "Apply as a Maven")}
                                    </Link>
                                </div>
                                <p className="text-sm text-gray-500 mt-6">
                                    {t("home.testimonials.alreadyMember", "Already a member? Keep sharing feedback—your reviews unlock as soon as we verify them.")}
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </main>
        </>
    );
}
